/// Wire protocol for two-player peer-to-peer over BLE (and any other
/// transport — see `transport.ts`). The protocol layer knows nothing
/// about BLE specifics; it deals in `Envelope`s of typed JSON.
///
/// Each game (hangman, future battleships, …) defines its own
/// concrete `payload` shape but reuses the framing, version
/// negotiation and heartbeat below.

/// Fixed BLE service UUID for every nav-games multiplayer session.
/// All games share one service so peer discovery only has to advertise
/// one UUID. The game type is negotiated inside `Hello` after the
/// connection is up.
///
/// Generated with `crypto.randomUUID()` and frozen here — do not
/// regenerate, peers wouldn't find each other across versions.
export const SERVICE_UUID = "5a4f9a30-1b67-4c64-9b6d-3a8b56b3f3f1";

/// Characteristic that the **host** writes to. Guest subscribes via
/// notify to receive host messages.
export const CHAR_HOST_TO_GUEST = "5a4f9a31-1b67-4c64-9b6d-3a8b56b3f3f1";

/// Characteristic that the **guest** writes to. Host subscribes via
/// notify to receive guest messages.
export const CHAR_GUEST_TO_HOST = "5a4f9a32-1b67-4c64-9b6d-3a8b56b3f3f1";

/// Maximum payload bytes per BLE write. The conservative default of
/// 180 leaves room for the 23-byte BLE ATT MTU floor + transport
/// overhead. Larger envelopes are chunked at the transport layer.
export const MAX_FRAME_BYTES = 180;

/// Protocol version. Both peers send their version in `Hello`; the
/// lower of the two governs the session.
export const PROTOCOL_VERSION = 1;

/// Role each peer takes at session start. Hangman uses `host` for the
/// player choosing the word and `guest` for the guesser; future games
/// re-use the same labels.
export type Role = "host" | "guest";

/// Discriminated union of every protocol-level message. Game-specific
/// payloads ride inside `GameMessage.payload` so the protocol layer
/// can stay game-agnostic.
export type Message =
  | { type: "hello"; version: number; gameType: string }
  | { type: "ready" }
  | { type: "heartbeat"; at: number }
  | { type: "bye"; reason?: string }
  | { type: "game"; payload: unknown };

export interface Envelope {
  /// Monotonically increasing per-peer sequence number. Used by the
  /// transport to detect dropped frames and by the receiver to
  /// dedupe retransmits.
  seq: number;
  /// Wall-clock millis. Useful for latency diagnostics.
  at: number;
  /// The payload itself.
  msg: Message;
}

/// Encode an envelope to UTF-8 bytes suitable for a BLE write.
/// Throws if the encoded payload exceeds `MAX_FRAME_BYTES` — the
/// caller is responsible for chunking or rejecting oversize content.
export function encodeEnvelope(env: Envelope): Uint8Array {
  const json = JSON.stringify(env);
  const bytes = new TextEncoder().encode(json);
  if (bytes.byteLength > MAX_FRAME_BYTES) {
    throw new ProtocolError(
      `envelope too large (${bytes.byteLength} > ${MAX_FRAME_BYTES})`,
    );
  }
  return bytes;
}

/// Decode a UTF-8 byte buffer back to an `Envelope`. Throws
/// `ProtocolError` on malformed JSON or missing required fields.
export function decodeEnvelope(bytes: Uint8Array): Envelope {
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(bytes));
  } catch (e) {
    throw new ProtocolError("bad json: " + (e as Error).message);
  }
  if (!isEnvelope(parsed)) {
    throw new ProtocolError("bad envelope shape");
  }
  return parsed;
}

function isEnvelope(v: unknown): v is Envelope {
  if (!v || typeof v !== "object") return false;
  const e = v as Record<string, unknown>;
  if (typeof e.seq !== "number" || typeof e.at !== "number") return false;
  if (!e.msg || typeof e.msg !== "object") return false;
  const m = e.msg as Record<string, unknown>;
  return typeof m.type === "string";
}

export class ProtocolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProtocolError";
  }
}
